import {
    BILLING_EMAIL,
    CONTACT_EMAIL,
    LEGAL_EMAIL,
    PARTNERSHIPS_EMAIL,
    SECURITY_EMAIL,
    SUPPORT_EMAIL,
    NOREPLY_EMAIL,
} from "@/lib/contactEmails";
import { ContactMailLink } from "@/components/ContactMailLink";
import { LegalPageShell } from "@/components/LegalPageShell";

const EFFECTIVE_DATE = "May 22, 2026";

export function PrivacyPolicyPage() {
    return (
        <LegalPageShell title="Privacy Policy" effectiveDate={EFFECTIVE_DATE} testId="privacy-page">
            <section>
                <h2 className="font-heading text-lg text-white">1. Overview</h2>
                <p className="mt-3">
                    FairShare (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) respects your privacy. This Privacy Policy explains how we
                    collect, use, disclose, and protect personal information when you use the FairShare website, applications,
                    and related services (the &quot;Service&quot;).
                </p>
                <p className="mt-3">
                    By using the Service, you agree to this Privacy Policy. If you do not agree, please do not use the Service.
                </p>
            </section>

            <section>
                <h2 className="font-heading text-lg text-white">2. Information We Collect</h2>
                <ul className="mt-3 list-disc space-y-2 pl-5">
                    <li><strong className="text-white/85">Account data:</strong> name, email, password hash, profile preferences, workspace memberships.</li>
                    <li><strong className="text-white/85">Workspace & pool data:</strong> organization names, pool configurations, allocations, approvals, audit events.</li>
                    <li><strong className="text-white/85">Payment data:</strong> billing status, Stripe customer/subscription identifiers, transaction metadata. Payment card details are processed by Stripe — we do not store full card numbers.</li>
                    <li><strong className="text-white/85">Bank/payout metadata:</strong> limited account identifiers (for example, last four digits) where you provide them for payouts.</li>
                    <li><strong className="text-white/85">Technical data:</strong> IP address, browser/device type, timestamps, and usage logs for security and reliability.</li>
                    <li><strong className="text-white/85">Communications:</strong> support requests and emails you send to us.</li>
                </ul>
            </section>

            <section>
                <h2 className="font-heading text-lg text-white">3. How We Use Information</h2>
                <p className="mt-3">We use personal information to:</p>
                <ul className="mt-3 list-disc space-y-2 pl-5">
                    <li>Provide, operate, and improve the Service;</li>
                    <li>Authenticate users and enforce security controls (including two-factor authentication);</li>
                    <li>Process subscriptions, client payments, platform fees, and payout records;</li>
                    <li>Send transactional emails (invitations, security notices, billing updates);</li>
                    <li>Maintain audit logs and investigate abuse or security incidents;</li>
                    <li>Comply with legal obligations and enforce our Terms of Service.</li>
                </ul>
            </section>

            <section>
                <h2 className="font-heading text-lg text-white">4. Legal Bases (where applicable)</h2>
                <p className="mt-3">
                    Depending on your location, we process personal data based on contract performance (providing the Service),
                    legitimate interests (security, fraud prevention, product improvement), legal obligations, and consent where
                    required (for example, optional marketing communications).
                </p>
            </section>

            <section>
                <h2 className="font-heading text-lg text-white">5. Sharing and Processors</h2>
                <p className="mt-3">We may share information with:</p>
                <ul className="mt-3 list-disc space-y-2 pl-5">
                    <li><strong className="text-white/85">Payment processors</strong> (such as Stripe) for billing, Connect onboarding, and client checkout;</li>
                    <li><strong className="text-white/85">Email providers</strong> (such as Resend) to deliver transactional messages from <ContactMailLink email={NOREPLY_EMAIL} />;</li>
                    <li><strong className="text-white/85">Infrastructure providers</strong> hosting our application and database;</li>
                    <li><strong className="text-white/85">Workspace collaborators</strong> you invite, according to your role and permissions;</li>
                    <li><strong className="text-white/85">Authorities</strong> when required by law or to protect rights, safety, and security.</li>
                </ul>
                <p className="mt-3">We do not sell your personal information.</p>
            </section>

            <section>
                <h2 className="font-heading text-lg text-white">6. Data Retention</h2>
                <p className="mt-3">
                    We retain account and workspace data while your account is active and as needed to provide the Service.
                    Audit logs and payout records may be retained longer where required for compliance, dispute resolution, or
                    legitimate business purposes. You may request deletion subject to legal and contractual limits.
                </p>
            </section>

            <section>
                <h2 className="font-heading text-lg text-white">7. Security</h2>
                <p className="mt-3">
                    We implement reasonable technical and organizational safeguards, including encryption in transit, access
                    controls, and audit logging. No method of transmission or storage is 100% secure. Report suspected security
                    issues to <ContactMailLink email={SECURITY_EMAIL} />.
                </p>
            </section>

            <section>
                <h2 className="font-heading text-lg text-white">8. Your Rights</h2>
                <p className="mt-3">
                    Depending on your jurisdiction, you may have rights to access, correct, delete, restrict, or export your personal
                    data, and to object to certain processing. Contact{" "}
                    <ContactMailLink email={LEGAL_EMAIL} /> to exercise these rights. We may verify your identity before responding.
                </p>
            </section>

            <section>
                <h2 className="font-heading text-lg text-white">9. International Transfers</h2>
                <p className="mt-3">
                    Your information may be processed in countries other than your own. Where required, we use appropriate safeguards
                    for cross-border transfers.
                </p>
            </section>

            <section>
                <h2 className="font-heading text-lg text-white">10. Cookies and Analytics</h2>
                <p className="mt-3">
                    We use essential cookies and similar technologies for authentication and session management. We may use analytics
                    tools to understand product usage. You can control non-essential cookies through your browser settings where
                    applicable.
                </p>
            </section>

            <section>
                <h2 className="font-heading text-lg text-white">11. Children</h2>
                <p className="mt-3">
                    The Service is not intended for users under 18. We do not knowingly collect personal information from children.
                </p>
            </section>

            <section>
                <h2 className="font-heading text-lg text-white">12. Changes</h2>
                <p className="mt-3">
                    We may update this Privacy Policy from time to time. Material changes will be posted on this page with an updated
                    effective date. Continued use of the Service after changes take effect constitutes acceptance.
                </p>
            </section>

            <section>
                <h2 className="font-heading text-lg text-white">13. Contact</h2>
                <ul className="mt-3 space-y-2">
                    <li><strong className="text-white/85">General:</strong> <ContactMailLink email={CONTACT_EMAIL} /></li>
                    <li><strong className="text-white/85">Privacy & compliance:</strong> <ContactMailLink email={LEGAL_EMAIL} /></li>
                    <li><strong className="text-white/85">Account support:</strong> <ContactMailLink email={SUPPORT_EMAIL} /></li>
                    <li><strong className="text-white/85">Billing & subscriptions:</strong> <ContactMailLink email={BILLING_EMAIL} /></li>
                    <li><strong className="text-white/85">Security disclosures:</strong> <ContactMailLink email={SECURITY_EMAIL} /></li>
                    <li><strong className="text-white/85">Partnerships & integrations:</strong> <ContactMailLink email={PARTNERSHIPS_EMAIL} /></li>
                </ul>
            </section>
        </LegalPageShell>
    );
}
